/**
 * Why a conversation did not become a sale.
 *
 * ---------------------------------------------------------------------------
 * WHAT THIS IS FOR, AND WHY IT DOES NOT EXIST ANYWHERE ELSE
 * ---------------------------------------------------------------------------
 *
 * Every dialer and CRM in this industry ships a *contact-status* taxonomy:
 * no answer, voicemail, busy, wrong number, callback, not interested, sold. Read
 * that list again and notice what it describes — what happened to the CALL.
 * Exactly one entry, "not interested", touches the OUTCOME, and it collapses
 * every possible reason a person declined into a single bucket.
 *
 * That bucket is where most conversations go. So the question every agency owner
 * actually has — *why are we losing?* — is unanswerable in every system on the
 * market, including this one until now. What fills the vacuum is training
 * material: ranked lists of "the top five objections" with no frequency data
 * behind the ranking. A search of carrier research, LIMRA, the conversation-
 * intelligence vendors and the academic literature turns up no measured
 * breakdown of why an engaged final-expense prospect says no. Not a weak one. None.
 *
 * So the first agency to record it properly is not catching up to anybody. It is
 * measuring something the industry has never measured.
 *
 * ---------------------------------------------------------------------------
 * THE THREE DESIGN DECISIONS THAT MAKE IT WORTH ANYTHING
 * ---------------------------------------------------------------------------
 *
 * 1. A REASON IS NOT A LABEL. IT IS A PREDICTION.
 *    "Not interested" tells you nothing about tomorrow. "They get paid on the
 *    3rd and asked me to call back after" tells you exactly when to ring and
 *    what to open with. So every reason here carries what follows from it:
 *    whether it can change, roughly when, and whether it says anything about
 *    the lead itself rather than the person. A taxonomy that does not predict
 *    is a filing system.
 *
 * 2. SOME REASONS ARE ABOUT THE LEAD, NOT THE PERSON.
 *    "I never filled out any form" is not an objection. It is a fact about who
 *    sold you the lead. In August 2025 the FTC settled with a lead marketplace
 *    for $145M over exactly this: ads for a fake "give back program", a paid
 *    doctor promising cover for a dollar a day, consumers who had no idea an
 *    agent would ring. A consent certificate proves a form was submitted; it
 *    cannot prove the person knew what they were submitting. `vendorSignal`
 *    marks the reasons that measure the supply chain, and no product captures
 *    that rate as a field.
 *
 * 3. THE AGENT IS NOT A NEUTRAL WITNESS.
 *    The reason is reported by the person whose performance it reflects, right
 *    after a call that did not go well. That is not dishonesty, it is the
 *    ordinary shape of self-report — and it means a taxonomy filled in by hand
 *    alone measures how agents describe their losses. So this module is built
 *    to hold TWO answers per call: the one the agent gave, and the one derived
 *    from what was actually said. Where they agree, the number is trustworthy.
 *    Where they diverge, the divergence is itself the most useful thing on the
 *    page — an agent who records "not interested" on calls where the transcript
 *    says "cannot afford it this month" is burning recoverable business and
 *    nobody, including them, can currently see it.
 *
 * This module is pure: no clock, no database, no I/O. It is the vocabulary and
 * the arithmetic; the service layer supplies the facts.
 */

// --------------------------------------------------------------- the reasons ---

/**
 * Why the sale did not happen, at the resolution a decision can be made from.
 *
 * Grouped by WHO OR WHAT the reason is really about, because that is what
 * decides who should act on it — the agent, the underwriter, the person who
 * buys the leads, or nobody.
 */
export const LOSS_REASONS = [
  // ---- the lead was never what it was sold as -------------------------------
  /** Reached a real person who is not the lead. Data hygiene, pure and simple. */
  'not_the_person',
  /**
   * "I never filled out anything." The single most valuable field in this file.
   *
   * It is the fingerprint of a lead sold on a pretext, and it is invisible in
   * every other system because there is nowhere to put it. Rate per vendor is
   * the closest thing to an honest quality score that exists.
   */
  'no_recollection',
  /** Somebody else on the same shared lead got there first. */
  'bought_elsewhere',

  // ---- they cannot buy ------------------------------------------------------
  /** Health makes them ineligible for what this agency can write. */
  'health_ineligible',
  /** Outside the issue ages on every product available. */
  'age_ineligible',
  /** Genuinely covered, and enough. Not the same as "has some". */
  'already_covered',
  /**
   * Cannot afford the premium at any face amount worth writing.
   *
   * Distinct from `money_timing` below, and the distinction is the whole point:
   * one is a fact about their income, the other is a fact about the calendar.
   */
  'cannot_afford',

  // ---- they cannot buy TODAY (the recoverable middle) -----------------------
  /** The person who decides was not on the phone. */
  'needs_spouse',
  /** Wants to think. No specific objection given. */
  'wants_time',
  /**
   * "Call me after I get paid." Money exists, the date does not yet.
   *
   * The most recoverable reason in this list and the one most often thrown away
   * as "not interested", because it FEELS like a no on the call.
   */
  'money_timing',
  /** Wants something in writing before they will talk about it. */
  'wants_it_in_writing',

  // ---- they will not buy FROM US -------------------------------------------
  /**
   * Will not give a social security number or bank details to a caller.
   *
   * NOT AN OBJECTION TO OVERCOME, and it is a mistake to script against it.
   * Adults over 60 reported $7.7 billion in fraud losses in 2025, up about 60%
   * in a year. Refusing to hand banking details to an unexpected caller is the
   * correct behaviour for this exact demographic. It is a trust threshold, and
   * the ways through it are a callback they initiate, something in writing, or
   * a local agent — never persistence.
   */
  'distrusts_phone',
  /** Wants to sit across a table from somebody. */
  'wants_local_agent',
  /** Had a bad experience with this agency, this industry, or a caller like us. */
  'bad_experience',

  // ---- there was no conversation to lose -----------------------------------
  /** Ended the call before there was anything to discuss. */
  'hung_up',
  /** Hostile. Recorded plainly because it is a real safety and coaching fact. */
  'hostile',
  /** Asked not to be called. Terminal, and it is honoured, not analysed. */
  'asked_to_stop',

  // ---- and the honest escape hatch -----------------------------------------
  /**
   * None of the above.
   *
   * Every taxonomy needs one, and a taxonomy whose "other" bucket grows is
   * telling you it is wrong. `otherRate` exists to make that visible rather
   * than comfortable.
   */
  'other',
] as const;

export type LossReason = (typeof LOSS_REASONS)[number];

/** Who or what a reason is really about. Decides whose problem it is. */
export type LossSubject = 'lead' | 'eligibility' | 'timing' | 'trust' | 'no_conversation' | 'unknown';

export interface LossMeaning {
  subject: LossSubject;
  /** What a person should read on the screen. */
  label: string;
  /**
   * Whether ringing again could plausibly produce a different answer.
   *
   * Deliberately conservative. Calling a recoverable reason terminal costs one
   * sale; calling a terminal reason recoverable costs somebody being rung about
   * something they cannot buy, which is how an agency earns a complaint.
   */
  recoverable: boolean;
  /**
   * Days before it is worth trying again, when it is worth trying again.
   *
   * A DEFAULT, not a rule — `money_timing` should use the date the person
   * actually named, and does whenever they named one. This is what to do when
   * nobody wrote a date down.
   */
  retryAfterDays: number | null;
  /** Never contact again for any campaign. Only ever true for a refusal. */
  suppresses: boolean;
  /**
   * True when the reason says more about who sold the lead than about the
   * person who answered. These are the rates worth putting in front of a vendor.
   */
  vendorSignal: boolean;
}

/**
 * What each reason means, exhaustively.
 *
 * A `Record` keyed by the union rather than a lookup with a default, so adding
 * a reason to `LOSS_REASONS` fails to compile until somebody has said what
 * follows from it. A reason with no consequence attached is a label, and this
 * file exists to not have labels.
 */
export const LOSS_MEANING: Record<LossReason, LossMeaning> = {
  not_the_person: {
    subject: 'lead', label: 'Not the person on the record',
    recoverable: false, retryAfterDays: null, suppresses: false, vendorSignal: true,
  },
  no_recollection: {
    subject: 'lead', label: 'Does not remember asking for a quote',
    /*
     * Not recoverable, and that is the conservative call. Somebody who does not
     * know why they are being rung has, as far as they are concerned, been cold
     * called — and ringing them again is the fact pattern in every complaint.
     */
    recoverable: false, retryAfterDays: null, suppresses: false, vendorSignal: true,
  },
  bought_elsewhere: {
    subject: 'lead', label: 'Already bought from somebody else',
    recoverable: false, retryAfterDays: null, suppresses: false, vendorSignal: true,
  },

  health_ineligible: {
    subject: 'eligibility', label: 'Health rules them out',
    /*
     * Twelve months, because carriers change what they will write and a
     * condition can move from decline to graded. It is a real second chance and
     * a slow one.
     */
    recoverable: true, retryAfterDays: 365, suppresses: false, vendorSignal: false,
  },
  age_ineligible: {
    subject: 'eligibility', label: 'Outside the issue ages',
    recoverable: false, retryAfterDays: null, suppresses: false, vendorSignal: false,
  },
  already_covered: {
    subject: 'eligibility', label: 'Already has enough cover',
    recoverable: true, retryAfterDays: 365, suppresses: false, vendorSignal: false,
  },
  cannot_afford: {
    subject: 'eligibility', label: 'Cannot afford any premium worth writing',
    recoverable: true, retryAfterDays: 180, suppresses: false, vendorSignal: false,
  },

  needs_spouse: {
    subject: 'timing', label: 'The decision-maker was not there',
    /*
     * Three days. Short on purpose: the conversation is still warm and the
     * person who was not on the call has just been told about it.
     */
    recoverable: true, retryAfterDays: 3, suppresses: false, vendorSignal: false,
  },
  wants_time: {
    subject: 'timing', label: 'Wants to think about it',
    recoverable: true, retryAfterDays: 7, suppresses: false, vendorSignal: false,
  },
  money_timing: {
    subject: 'timing', label: 'Money is there on a date that has not come yet',
    /*
     * Fourteen days is the fallback for a benefit cycle when nobody wrote down
     * the date. When they DID name one, the promise carries it and this is
     * never consulted.
     */
    recoverable: true, retryAfterDays: 14, suppresses: false, vendorSignal: false,
  },
  wants_it_in_writing: {
    subject: 'timing', label: 'Wants it in writing first',
    recoverable: true, retryAfterDays: 5, suppresses: false, vendorSignal: false,
  },

  distrusts_phone: {
    subject: 'trust', label: 'Will not do this over the telephone',
    /*
     * Recoverable, but not by ringing back with the same approach — the way
     * through is something in writing or a person they can meet. Thirty days
     * and a different opening, or leave them alone.
     */
    recoverable: true, retryAfterDays: 30, suppresses: false, vendorSignal: false,
  },
  wants_local_agent: {
    subject: 'trust', label: 'Wants somebody local, face to face',
    recoverable: true, retryAfterDays: 30, suppresses: false, vendorSignal: false,
  },
  bad_experience: {
    subject: 'trust', label: 'Bad experience with us or with callers like us',
    recoverable: false, retryAfterDays: null, suppresses: false, vendorSignal: false,
  },

  hung_up: {
    subject: 'no_conversation', label: 'Hung up before there was a conversation',
    recoverable: true, retryAfterDays: 14, suppresses: false, vendorSignal: false,
  },
  hostile: {
    subject: 'no_conversation', label: 'Hostile',
    /*
     * Not suppressed automatically, because that decision belongs to a person —
     * but nothing about a hostile call suggests ringing again, so it is
     * terminal until somebody decides otherwise.
     */
    recoverable: false, retryAfterDays: null, suppresses: false, vendorSignal: false,
  },
  asked_to_stop: {
    subject: 'no_conversation', label: 'Asked not to be called again',
    recoverable: false, retryAfterDays: null, suppresses: true, vendorSignal: false,
  },

  other: {
    subject: 'unknown', label: 'Something else',
    recoverable: true, retryAfterDays: 30, suppresses: false, vendorSignal: false,
  },
};

/** The reasons that say something about who sold the lead. */
export const VENDOR_SIGNAL_REASONS: readonly LossReason[] =
  LOSS_REASONS.filter((r) => LOSS_MEANING[r].vendorSignal);

/** The reasons worth ringing again about. */
export const RECOVERABLE_REASONS: readonly LossReason[] =
  LOSS_REASONS.filter((r) => LOSS_MEANING[r].recoverable);

// ------------------------------------------------------------ one outcome ---

/**
 * One conversation that did not close, as recorded.
 *
 * `saidByAgent` and `heardOnCall` are BOTH optional and BOTH kept. Either alone
 * is a partial view: the agent knows things the transcript cannot carry (a tone,
 * a child in the background, what they meant), and the transcript is not
 * defending a scorecard.
 */
export interface RecordedLoss {
  /** What the agent chose. Null when they skipped it — which is itself data. */
  saidByAgent: LossReason | null;
  /** What the transcript supports. Null when there was no transcript to read. */
  heardOnCall: LossReason | null;
  /** Which agent. */
  agentId: string;
  /** Which lead vendor or list this record came from, when it is known. */
  sourceId: string | null;
}

/**
 * The reason to act on, given both accounts.
 *
 * THE AGENT WINS TIES AND NEAR-TIES, DELIBERATELY. They were on the call. The
 * derived reason exists to be COMPARED with theirs, not to overrule it — a
 * system that silently replaced an agent's judgement with a machine's reading
 * of a transcript would be wrong about a quiet fraction of calls and would have
 * destroyed the one number that makes the whole thing trustworthy, which is how
 * often the two agree.
 *
 * The derived reason is used only where the agent gave none.
 */
export function reasonToActOn(loss: RecordedLoss): LossReason | null {
  return loss.saidByAgent ?? loss.heardOnCall;
}

/** Whether the two accounts of the same call disagree. Null when only one exists. */
export function diverges(loss: RecordedLoss): boolean | null {
  if (!loss.saidByAgent || !loss.heardOnCall) return null;
  return loss.saidByAgent !== loss.heardOnCall;
}

// -------------------------------------------------------------- the counts ---

export interface ReasonCount {
  reason: LossReason;
  count: number;
  /** Share of the losses this breakdown covers, 0–1. */
  share: number;
}

export interface LossBreakdown {
  total: number;
  byReason: ReasonCount[];
  /**
   * Share that came back `other`, 0–1.
   *
   * The taxonomy's own error bar. A rising `other` means these categories have
   * stopped describing what is happening on the telephone, and the honest
   * response is to change them rather than to keep filing.
   */
  otherRate: number;
  /**
   * Share the agent left blank, 0–1.
   *
   * Not scolding: a floor that skips the reason on a third of its losses has a
   * screen problem or a time problem, and the number says which before anybody
   * argues about discipline.
   */
  unrecordedRate: number;
}

const EMPTY: LossBreakdown = { total: 0, byReason: [], otherRate: 0, unrecordedRate: 0 };

/**
 * What this set of losses was made of.
 *
 * Ordered by count, then by name so equal counts do not shuffle between page
 * loads — a report that reorders itself is one nobody reads twice.
 */
export function breakdown(losses: readonly RecordedLoss[]): LossBreakdown {
  if (!losses.length) return EMPTY;

  const counts = new Map<LossReason, number>();
  let unrecorded = 0;
  for (const loss of losses) {
    const reason = reasonToActOn(loss);
    if (!reason) { unrecorded += 1; continue; }
    counts.set(reason, (counts.get(reason) ?? 0) + 1);
  }

  const attributed = losses.length - unrecorded;
  const byReason: ReasonCount[] = [...counts.entries()]
    .map(([reason, count]) => ({ reason, count, share: attributed ? count / attributed : 0 }))
    .sort((a, b) => b.count - a.count || (a.reason < b.reason ? -1 : 1));

  return {
    total: losses.length,
    byReason,
    otherRate: attributed ? (counts.get('other') ?? 0) / attributed : 0,
    unrecordedRate: losses.length ? unrecorded / losses.length : 0,
  };
}

// ------------------------------------------------------- the vendor's score ---

export interface SourceQuality {
  sourceId: string;
  /** Losses recorded against this source in the window. */
  losses: number;
  /** How many carried a reason that is about the lead rather than the person. */
  badLead: number;
  /** `badLead / losses`, 0–1. Null below the floor — see `MIN_FOR_A_RATE`. */
  badLeadRate: number | null;
  /** The single most valuable number here: "I never asked for a quote". */
  noRecollection: number;
  noRecollectionRate: number | null;
  /** True when there is not yet enough to say anything. */
  thin: boolean;
}

/**
 * Below this, no rate is stated at all.
 *
 * Thirty, because a vendor conversation that starts "four of your leads didn't
 * remember filling anything in" is a conversation about four people. The same
 * discipline the contact-rate report uses, for the same reason: a number quoted
 * at a sample size that cannot carry it is worse than no number, because it
 * gets quoted.
 */
export const MIN_FOR_A_RATE = 30;

/**
 * What the losses say about who sold the leads.
 *
 * THE CAVEAT THAT HAS TO TRAVEL WITH THIS FUNCTION, and it is why the service
 * layer must randomise which agent gets which lead: unless assignment is random,
 * a vendor comparison measures the agents who happened to work that vendor's
 * leads, not the vendor. Every product that reports "conversion by lead source"
 * has this hole in it and none of them mention it. `badLeadRate` is the
 * exception — "this is not the right person" and "I never filled anything in"
 * are facts about the record, not about who rang, so they survive a confounded
 * assignment. Those are the two numbers to put in front of a vendor.
 */
export function sourceQuality(losses: readonly RecordedLoss[]): SourceQuality[] {
  const bySource = new Map<string, RecordedLoss[]>();
  for (const loss of losses) {
    if (!loss.sourceId) continue;
    const bucket = bySource.get(loss.sourceId);
    if (bucket) bucket.push(loss); else bySource.set(loss.sourceId, [loss]);
  }

  const out: SourceQuality[] = [];
  for (const [sourceId, rows] of bySource) {
    let badLead = 0;
    let noRecollection = 0;
    for (const row of rows) {
      const reason = reasonToActOn(row);
      if (!reason) continue;
      if (LOSS_MEANING[reason].vendorSignal) badLead += 1;
      if (reason === 'no_recollection') noRecollection += 1;
    }
    const thin = rows.length < MIN_FOR_A_RATE;
    out.push({
      sourceId,
      losses: rows.length,
      badLead,
      badLeadRate: thin ? null : badLead / rows.length,
      noRecollection,
      noRecollectionRate: thin ? null : noRecollection / rows.length,
      thin,
    });
  }
  return out.sort((a, b) => (b.badLeadRate ?? -1) - (a.badLeadRate ?? -1)
    || (a.sourceId < b.sourceId ? -1 : 1));
}

// -------------------------------------------------------- the agent's mirror ---

export interface AgentAgreement {
  agentId: string;
  /** Calls where both an agent reason and a derived reason exist. */
  comparable: number;
  agreed: number;
  /** `agreed / comparable`, 0–1. Null below `MIN_FOR_A_RATE`. */
  agreementRate: number | null;
  /**
   * Where they most often diverge, and in which direction.
   *
   * Not a score. This is the one report in the system whose purpose is a
   * conversation rather than a ranking, and it is deliberately shaped as
   * "here is a pattern in your calls" rather than "here is how wrong you are".
   */
  topDivergence: { saidByAgent: LossReason; heardOnCall: LossReason; count: number } | null;
  thin: boolean;
}

/**
 * How often each agent's account of a loss matches what the call actually held.
 *
 * WHAT THIS IS AND IS NOT
 * It is not a lie detector, and it must never be presented as one. Two honest
 * people can hear the same call differently, and the agent has information the
 * transcript does not carry. What it is good for is one specific and expensive
 * pattern: an agent who records recoverable losses as terminal ones. Somebody
 * filing "not interested" on calls where the person said they get paid on the
 * third is throwing away the most winnable business in the book, every day, in
 * good faith, and there is currently no way for them or anyone else to notice.
 */
export function agentAgreement(losses: readonly RecordedLoss[]): AgentAgreement[] {
  const byAgent = new Map<string, RecordedLoss[]>();
  for (const loss of losses) {
    const bucket = byAgent.get(loss.agentId);
    if (bucket) bucket.push(loss); else byAgent.set(loss.agentId, [loss]);
  }

  const out: AgentAgreement[] = [];
  for (const [agentId, rows] of byAgent) {
    const comparable = rows.filter((r) => diverges(r) !== null);
    const agreed = comparable.filter((r) => diverges(r) === false).length;

    const pairs = new Map<string, { saidByAgent: LossReason; heardOnCall: LossReason; count: number }>();
    for (const row of comparable) {
      if (diverges(row) !== true) continue;
      const key = `${row.saidByAgent!}>${row.heardOnCall!}`;
      const seen = pairs.get(key);
      if (seen) seen.count += 1;
      else pairs.set(key, { saidByAgent: row.saidByAgent!, heardOnCall: row.heardOnCall!, count: 1 });
    }
    const top = [...pairs.values()].sort(
      (a, b) => b.count - a.count
        || (a.saidByAgent < b.saidByAgent ? -1 : a.saidByAgent > b.saidByAgent ? 1 : 0)
        || (a.heardOnCall < b.heardOnCall ? -1 : 1),
    )[0] ?? null;

    const thin = comparable.length < MIN_FOR_A_RATE;
    out.push({
      agentId,
      comparable: comparable.length,
      agreed,
      agreementRate: thin || !comparable.length ? null : agreed / comparable.length,
      topDivergence: top,
      thin,
    });
  }
  return out.sort((a, b) => (a.agreementRate ?? 2) - (b.agreementRate ?? 2)
    || (a.agentId < b.agentId ? -1 : 1));
}

// ------------------------------------------------------- what to do next ----

/**
 * When this person becomes worth ringing again, if ever.
 *
 * `promisedAt` always wins. A date the person named on the telephone beats every
 * default in this file, because it is the one piece of timing information that
 * came from them rather than from us.
 */
export function retryAt(
  reason: LossReason, lostAt: Date, promisedAt: Date | null = null,
): Date | null {
  if (promisedAt) return promisedAt;
  const meaning = LOSS_MEANING[reason];
  if (!meaning.recoverable || meaning.retryAfterDays === null) return null;
  return new Date(lostAt.getTime() + meaning.retryAfterDays * 86_400_000);
}

/** Whether this reason means never contacting them again about anything. */
export function suppresses(reason: LossReason): boolean {
  return LOSS_MEANING[reason].suppresses;
}
